import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { bulkUpdateInvoicesToPaidMutation } from 'graphql-queries';
import {
  BulkUpdateInvoicesToPaidError,
  BulkUpdateInvoicesToPaidInput,
  BulkUpdateInvoicesToPaidRequest,
  BulkUpdateInvoicesToPaidResult,
} from './bulkUpdateInvoicesToPaidTypes';
import {
  isBulkUpdateInvoicesToPaidResponse,
  mapBulkUpdateInvoicesToPaidResponse,
} from './bulkUpdateInvoicesToPaidMappers';
import { getActivateUserActorType } from './externalActorTypeMapper';

// Re-export types for backward compatibility
export type {
  BulkUpdateInvoicesToPaidError,
  BulkUpdateInvoicesToPaidInput,
  BulkUpdateInvoicesToPaidRequest,
  BulkUpdateInvoicesToPaidResponse,
  BulkUpdateInvoicesToPaidResult,
} from './bulkUpdateInvoicesToPaidTypes';
export { mapBulkUpdateInvoicesToPaidResponse } from './bulkUpdateInvoicesToPaidMappers';

export const useBulkUpdateInvoicesToPaid = () => {
  const [mutateBulkUpdateInvoicesToPaid, { loading, error }] = useMutation(
    bulkUpdateInvoicesToPaidMutation
  );
  const [thrownError, setThrownError] = useState<
    BulkUpdateInvoicesToPaidError | undefined
  >();

  const bulkUpdateInvoicesToPaid = async ({
    invoiceIds,
    externalActorId,
  }: BulkUpdateInvoicesToPaidInput): Promise<BulkUpdateInvoicesToPaidResult> => {
    // Clear any previous thrown error
    setThrownError(undefined);

    const bulkUpdateInvoicesToPaidRequest: BulkUpdateInvoicesToPaidRequest = {
      invoiceIds,
      externalActorId,
      externalActorType: getActivateUserActorType(),
    };

    try {
      const result = await mutateBulkUpdateInvoicesToPaid({
        variables: {
          bulkUpdateInvoicesToPaidRequest,
        },
      });

      const rawResponse = result.data?.bulkUpdateInvoicesToPaid;

      if (!rawResponse || !isBulkUpdateInvoicesToPaidResponse(rawResponse)) {
        throw new BulkUpdateInvoicesToPaidError(
          'No response data received from server'
        );
      }

      return mapBulkUpdateInvoicesToPaidResponse(rawResponse);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to bulk update invoices to paid';

      const bulkUpdateError = new BulkUpdateInvoicesToPaidError(message, {
        cause: err,
      });

      setThrownError(bulkUpdateError);

      throw bulkUpdateError;
    }
  };

  const bulkUpdateInvoicesToPaidError = useMemo(() => {
    // Prioritize thrown error over mutation hook error
    if (thrownError) return thrownError;

    if (!error) return undefined;

    const message =
      error instanceof Error && error.message
        ? error.message
        : 'Failed to bulk update invoices to paid';

    return new BulkUpdateInvoicesToPaidError(message, { cause: error });
  }, [thrownError, error]);

  return {
    bulkUpdateInvoicesToPaid,
    loading,
    error: bulkUpdateInvoicesToPaidError,
  };
};

